import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LIVE_LIST_OPTIONS } from '../../utils/liveList';
import { useNavigate } from 'react-router-dom';
import { facturationService } from '../../services/api';
import { Facture, PaginatedResponse } from '@/types';
import { Search, Plus, Eye, CreditCard, AlertCircle, CheckCircle, Trash2, Printer, Receipt, Calendar, X, Wallet, FileText } from 'lucide-react';
import Pagination from '../common/Pagination';
import FactureForm from './FactureForm';
import { useAuth } from '../../contexts/AuthContext';
import { hasRole } from '../../utils/roles';

const FacturationList: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dateFilter, setDateFilter] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [selectedFacture, setSelectedFacture] = useState<Facture | null>(null);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize = 25;

  // Fonction pour obtenir les dates selon le filtre rapide
  const getDateRange = (filter: string): { start: string; end: string } | null => {
    const today = new Date();
    const start = new Date();
    const end = new Date();

    switch (filter) {
      case 'today':
        return {
          start: today.toISOString().split('T')[0],
          end: today.toISOString().split('T')[0]
        };
      case 'yesterday':
        start.setDate(today.getDate() - 1);
        return {
          start: start.toISOString().split('T')[0],
          end: start.toISOString().split('T')[0]
        };
      case 'week':
        start.setDate(today.getDate() - 7);
        return {
          start: start.toISOString().split('T')[0],
          end: today.toISOString().split('T')[0]
        };
      case 'month':
        start.setMonth(today.getMonth() - 1);
        return {
          start: start.toISOString().split('T')[0],
          end: today.toISOString().split('T')[0]
        };
      case 'custom':
        if (startDate && endDate) {
          return { start: startDate, end: endDate };
        }
        return null;
      default:
        return null;
    }
  };

  const dateRange = getDateRange(dateFilter);

  const { data, isLoading, refetch } = useQuery<PaginatedResponse<Facture>>(
    ['factures', { search: searchTerm, status: statusFilter, dateFilter, startDate, endDate, page: currentPage }],
    () => facturationService.getFactures({
      search: searchTerm,
      page: currentPage,
      page_size: pageSize,
      ordering: '-date_emission', // Tri par date décroissante (plus récent en premier)
      ...(statusFilter && { status: statusFilter }),
      ...(dateRange && { date_debut: dateRange.start, date_fin: dateRange.end })
    }).then(res => res.data),
    LIVE_LIST_OPTIONS
  );

  const { data: statistiques } = useQuery({
    queryKey: ['factures-statistiques'],
    queryFn: () => facturationService.getStatistiques().then(res => res.data),
    ...LIVE_LIST_OPTIONS,
  });

  const factures = data?.results || [];

  // Réinitialiser la page à 1 quand les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, dateFilter, startDate, endDate]);

  const getStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
      'brouillon': 'son-chip-gray',
      'emise': 'son-chip-blue',
      'payee': 'son-chip-green',
      'partiellement_payee': 'son-chip-amber',
      'prise_en_charge_assurance': 'son-chip-purple',
      'annulee': 'son-chip-red'
    };
    return colors[status] || 'son-chip-gray';
  };

  const getStatusLabel = (status: string): string => {
    const labels: Record<string, string> = {
      'brouillon': 'Brouillon',
      'emise': 'Émise',
      'payee': 'Payée',
      'partiellement_payee': 'Partiellement payée',
      'prise_en_charge_assurance': 'Prise en charge assurance',
      'annulee': 'Annulée'
    };
    return labels[status] || status;
  };

  const getStatusIcon = (status: string): React.ReactNode => {
    switch (status) {
      case 'payee':
      case 'prise_en_charge_assurance':
        return <CheckCircle className="h-3.5 w-3.5" aria-hidden />;
      case 'emise':
      case 'partiellement_payee':
        return <AlertCircle className="h-3.5 w-3.5" aria-hidden />;
      default:
        return <CreditCard className="h-3.5 w-3.5" aria-hidden />;
    }
  };

  const handleEdit = (facture: Facture): void => {
    setSelectedFacture(facture);
    setShowForm(true);
  };

  const handleCloseForm = (): void => {
    setShowForm(false);
    setSelectedFacture(null);
  };

  const handleSuccess = (): void => {
    refetch();
    handleCloseForm();
  };

  const handleDelete = async (facture: Facture): Promise<void> => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la facture ${facture.numero} ?`)) {
      return;
    }

    try {
      await facturationService.deleteFacture(facture.id);
      refetch();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression de la facture');
    }
  };

  const handlePrint = async (facture: Facture): Promise<void> => {
    try {
      const response = await facturationService.imprimerFacture(facture.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `facture_${facture.numero}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors de l\'impression:', error);
      alert('Erreur lors de l\'impression de la facture');
    }
  };

  const handleRecu = async (facture: Facture): Promise<void> => {
    try {
      const response = await facturationService.imprimerRecu(facture.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `recu_${facture.numero}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors de l\'édition du reçu:', error);
      alert('Erreur lors de l\'édition du reçu de paiement');
    }
  };

  if (showForm) {
    return (
      <FactureForm
        facture={selectedFacture || undefined}
        onClose={handleCloseForm}
        onSuccess={handleSuccess}
      />
    );
  }

  const hasFilters = Boolean(dateFilter || statusFilter || searchTerm);
  const resetFilters = (): void => {
    setSearchTerm('');
    setStatusFilter('');
    setDateFilter('');
    setStartDate('');
    setEndDate('');
  };

  const kpis = statistiques ? [
    { label: 'Total factures', value: statistiques.total_factures, icon: CreditCard, tone: 'bg-[color:var(--blue-soft)] text-[#1565B8]' },
    { label: 'Factures payées', value: statistiques.factures_payees, icon: CheckCircle, tone: 'bg-[color:var(--teal-soft)] text-[#0E8466]' },
    { label: 'Factures impayées', value: statistiques.factures_impayees, icon: AlertCircle, tone: 'bg-[#FCE4E1] text-[#C0392B]' },
    { label: 'Montant impayé', value: `${Number(statistiques.montant_impaye ?? 0).toLocaleString('fr-FR')} F`, icon: Wallet, tone: 'bg-[#FBEAD2] text-[#B5781A]' },
  ] : [];

  return (
    <div className="space-y-5">
      {/* En-tête */}
      <div className="son-page-head">
        <div>
          <h1 className="son-h1">Facturation</h1>
          <p className="son-sub">
            {data && `${data.count} facture${data.count > 1 ? 's' : ''} · `}Triées de la plus récente à la plus ancienne
          </p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary">
          <Plus className="h-4 w-4" aria-hidden />
          Nouvelle facture
        </button>
      </div>

      {/* Statistiques */}
      {statistiques && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {kpis.map((k) => (
            <div key={k.label} className="son-kpi">
              <div className={`son-kpi-icon ${k.tone}`}><k.icon className="h-5 w-5" aria-hidden /></div>
              <div className="min-w-0">
                <p className="son-kpi-label">{k.label}</p>
                <p className="son-kpi-value truncate">{k.value}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recherche & filtres */}
      <div className="son-toolbar">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-end">
          <div className="son-search">
            <label htmlFor="factureSearch" className="sr-only">Rechercher une facture</label>
            <Search aria-hidden />
            <input
              id="factureSearch"
              type="search"
              placeholder="N° de facture, patient…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input text-gray-700"
            />
          </div>

          <div className="lg:w-56">
            <label htmlFor="dateFilter" className="son-field-label">
              <Calendar className="inline h-3.5 w-3.5 mr-1 -mt-0.5" aria-hidden />
              Période
            </label>
            <select
              id="dateFilter"
              value={dateFilter}
              onChange={(e) => {
                setDateFilter(e.target.value);
                if (e.target.value !== 'custom') {
                  setStartDate('');
                  setEndDate('');
                }
              }}
              className="form-select text-gray-700"
            >
              <option value="">Toutes les dates</option>
              <option value="today">Aujourd'hui</option>
              <option value="yesterday">Hier</option>
              <option value="week">7 derniers jours</option>
              <option value="month">30 derniers jours</option>
              <option value="custom">Période personnalisée</option>
            </select>
          </div>

          {dateFilter === 'custom' && (
            <>
              <div className="lg:w-44">
                <label htmlFor="startDate" className="son-field-label">Date début</label>
                <input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="form-input text-gray-700"
                />
              </div>
              <div className="lg:w-44">
                <label htmlFor="endDate" className="son-field-label">Date fin</label>
                <input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="form-input text-gray-700"
                />
              </div>
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="son-seg" role="group" aria-label="Filtrer par statut">
            {[
              { value: '', label: 'Tous les statuts' },
              { value: 'brouillon', label: 'Brouillon' },
              { value: 'emise', label: 'Émise' },
              { value: 'partiellement_payee', label: 'Partiellement payée' },
              { value: 'payee', label: 'Payée' },
              { value: 'prise_en_charge_assurance', label: 'Prise en charge assurance' },
              { value: 'annulee', label: 'Annulée' },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                aria-pressed={statusFilter === opt.value}
                onClick={() => setStatusFilter(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {hasFilters && (
            <button onClick={resetFilters} className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-[color:var(--muted)] hover:text-[color:var(--text)] px-2 py-1.5 rounded-lg">
              <X className="h-3.5 w-3.5" aria-hidden />
              Réinitialiser les filtres
            </button>
          )}
        </div>
      </div>

      {/* Liste des factures */}
      <div className="son-table-wrap" aria-busy={isLoading}>
        <div className="overflow-x-auto">
          <table className="son-table">
            <thead>
              <tr>
                <th>Numéro</th>
                <th>Patient</th>
                <th>Date émission</th>
                <th className="!text-right">Montant total</th>
                <th>Statut</th>
                <th className="!text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading
                ? Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i}>
                      {[28, 44, 24, 30, 26, 18].map((w, j) => (
                        <td key={j}><div className="son-skeleton h-3.5" style={{ width: `${w * 3}px`, maxWidth: '100%' }} /></td>
                      ))}
                    </tr>
                  ))
                : factures.map((facture) => {
                    const nom = `${facture.patient_details?.nom ?? ''} ${facture.patient_details?.prenoms ?? ''}`.trim();
                    const initiales = `${facture.patient_details?.nom?.[0] ?? ''}${facture.patient_details?.prenoms?.[0] ?? ''}`.toUpperCase();
                    return (
                      <tr key={facture.id}>
                        <td className="whitespace-nowrap">
                          <span className="font-bold tabular-nums tracking-tight">{facture.numero}</span>
                        </td>
                        <td className="whitespace-nowrap">
                          <button
                            type="button"
                            className="flex items-center gap-2.5 text-left group"
                            onClick={() => navigate(`/patients/${facture.patient}`)}
                            title="Voir le dossier patient"
                          >
                            <span className="son-avatar" aria-hidden>{initiales || '?'}</span>
                            <span className="font-semibold group-hover:text-[color:var(--blue)] group-hover:underline">
                              {nom || '—'}
                            </span>
                          </button>
                        </td>
                        <td className="whitespace-nowrap tabular-nums text-[color:var(--muted)]">
                          {new Date(facture.date_emission).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="whitespace-nowrap text-right tabular-nums">
                          <div className="font-bold">{Number(facture.montant_total).toLocaleString('fr-FR')} F</div>
                          <div className="text-[11.5px] text-[color:var(--muted)]">
                            Patient : {Number(facture.montant_patient).toLocaleString('fr-FR')} F
                          </div>
                        </td>
                        <td className="whitespace-nowrap">
                          <span className={`son-chip ${getStatusColor(facture.status)}`}>
                            {getStatusIcon(facture.status)}
                            {getStatusLabel(facture.status)}
                          </span>
                        </td>
                        <td className="whitespace-nowrap">
                          <div className="flex justify-end gap-1">
                            <button
                              onClick={() => handleEdit(facture)}
                              className="son-icon-btn is-blue"
                              title="Voir/Modifier"
                              aria-label="Voir ou modifier la facture"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handlePrint(facture)}
                              className="son-icon-btn is-teal"
                              title="Imprimer la facture"
                              aria-label="Imprimer la facture"
                            >
                              <Printer className="h-4 w-4" />
                            </button>
                            {(facture.paiements?.length ?? 0) > 0 && (
                              <button
                                onClick={() => handleRecu(facture)}
                                className="son-icon-btn is-purple"
                                title="Reçu de paiement"
                                aria-label="Reçu de paiement"
                              >
                                <Receipt className="h-4 w-4" />
                              </button>
                            )}
                            {hasRole(user, 'admin') && (
                              <button
                                onClick={() => handleDelete(facture)}
                                className="son-icon-btn is-red"
                                title="Supprimer"
                                aria-label="Supprimer la facture"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        </div>

        {!isLoading && factures.length === 0 && (
          <div className="son-empty">
            <div className="son-empty-icon"><FileText className="h-6 w-6" aria-hidden /></div>
            <p className="font-semibold text-[color:var(--text)]">Aucune facture trouvée</p>
            <p>{hasFilters ? 'Aucun résultat pour ces filtres.' : 'Les factures émises apparaîtront ici.'}</p>
            {hasFilters && (
              <button onClick={resetFilters} className="btn-secondary mt-2 text-sm">Réinitialiser les filtres</button>
            )}
          </div>
        )}

        {!isLoading && data && (
          <Pagination page={currentPage} pageSize={pageSize} count={data.count} label="factures" onChange={setCurrentPage} />
        )}
      </div>
    </div>
  );
};

export default FacturationList;